import { useState, useCallback, useEffect, useMemo } from 'react';
import { Video } from 'lucide-react';
import Breadcrumb from '../../components/UI/Breadcrumb/Breadcrumb';
import LoadingSpinner from '../../components/UI/LoadingSpinner/LoadingSpinner';
import ApiService from '../../services/api';
import './WorkflowResults.css';

const PAGE_SIZE = 12;

const getStatusPill = (status) => {
  switch (status) {
    case 4: return { label: '已完成',  cls: 'wr-pill--success' };
    case 3: return { label: '失败',    cls: 'wr-pill--failed' };
    case 2: return { label: '制作中',  cls: 'wr-pill--processing' };
    case 1: return { label: '排队中',  cls: 'wr-pill--queued' };
    default: return { label: '未知',   cls: 'wr-pill--queued' };
  }
};

// Normalize one item from any server response shape
const normalizeItem = (item, idx) => ({
  id:          item.id        || item.task_id  || `wf-${idx}`,
  title:       item.title     || item.name     || `工作流任务 ${idx + 1}`,
  avatarName:  item.avatarName  || item.avatar_name  || item.virtualman_name || '',
  voiceName:   item.voiceName   || item.voice_name   || item.timbre_name     || '',
  status:      item.status,
  createdAt:   item.createdAt  || item.created_at   || '',
  videoUrl:    item.videoUrl   || item.video_url     || item.url             || '',
  thumbnail:   item.thumbnail  || item.cover_url     || '',
  duration:    item.duration   || '',
});

// Extract items array from any server response shape
const extractItems = (data) => {
  const d = data?.data ?? data ?? {};
  const raw = d.items ?? d.list ?? d.records ?? d.results ?? [];
  return Array.isArray(raw) ? raw : [];
};

const extractTotal = (data) => {
  const d = data?.data ?? data ?? {};
  return Number(d.total ?? d.totalCount ?? d.count ?? 0) || 0;
};

const WorkflowResults = () => {
  const [items, setItems]           = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading]       = useState(false);
  const [apiError, setApiError]     = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  const breadcrumbItems = [
    { label: '首页', path: '/dashboard' },
    { label: '工作流结果', path: null },
  ];

  // ── Fetch ──
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setApiError('');
      try {
        const data = await ApiService.getWorkflowResults(currentPage, PAGE_SIZE);
        if (cancelled) return;
        if (data.code !== undefined && data.code !== 0) throw new Error(data.msg || `Error ${data.code}`);
        setItems(extractItems(data).map(normalizeItem));
        setTotalItems(extractTotal(data));
      } catch (err) {
        if (!cancelled) { setApiError(err.message || '加载失败'); setItems([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [currentPage]);

  // ── Client-side search filter ──
  const filtered = useMemo(() => {
    if (!searchQuery) return items;
    const q = searchQuery.toLowerCase();
    return items.filter((item) => item.title.toLowerCase().includes(q));
  }, [items, searchQuery]);

  const totalPages = Math.ceil(totalItems / PAGE_SIZE) || 1;

  // ── Reset page when search changes ──
  useEffect(() => { setCurrentPage(1); }, [searchQuery]);

  // ── Clamp page ──
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const handleSearch = useCallback((e) => setSearchQuery(e.target.value), []);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleDownload = (item) => {
    if (item.videoUrl) window.open(item.videoUrl, '_blank');
  };

  const handleDelete = () => {
    // TODO: 上线前需实现 DELETE /api/workflow/result/:id
    alert('删除功能需后端 API 支持，开发中');
  };

  return (
    <div className="wr-page">
      <Breadcrumb items={breadcrumbItems} />

      {/* Page Header */}
      <div className="wr-page-header">
        <h1 className="wr-page-title">工作流结果</h1>
      </div>

      {/* Toolbar */}
      <div className="wr-toolbar">
        <input
          className="wr-search-box"
          placeholder="搜索任务..."
          value={searchQuery}
          onChange={handleSearch}
        />
        <span className="wr-total-count">共 {totalItems} 个任务</span>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <LoadingSpinner />
        </div>
      ) : apiError ? (
        <div className="wr-empty">
          <p className="wr-empty-text" style={{ color: '#dc2626' }}>加载失败：{apiError}</p>
          <button
            style={{ marginTop: 8, fontSize: 13, color: '#4f46e5', background: 'none', border: 'none', cursor: 'pointer' }}
            onClick={() => setCurrentPage((p) => p)} // triggers re-fetch
          >重试</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="wr-empty">
          <Video size={48} className="wr-empty-icon" />
          <p className="wr-empty-text">
            {searchQuery ? '没有找到匹配的任务' : '还没有工作流任务，去生成视频吧'}
          </p>
        </div>
      ) : (
        <>
          <div className="wr-cards-grid">
            {filtered.map((item) => {
              const pill = getStatusPill(item.status);
              const isCompleted = item.status === 4;
              return (
                <div key={item.id} className="wr-card">
                  {/* Thumbnail */}
                  <div className="wr-thumb">
                    {isCompleted && item.videoUrl ? (
                      <video
                        className="wr-thumb-media"
                        src={item.videoUrl}
                        preload="metadata"
                        poster={item.thumbnail || undefined}
                      />
                    ) : item.thumbnail ? (
                      <img className="wr-thumb-media" src={item.thumbnail} alt={item.title} />
                    ) : (
                      <div className="wr-thumb-placeholder">
                        <Video size={36} />
                      </div>
                    )}
                    <span className={`wr-pill ${pill.cls}`}>{pill.label}</span>
                    {item.duration && (
                      <span className="wr-duration">{item.duration}</span>
                    )}
                  </div>

                  {/* Body */}
                  <div className="wr-body">
                    <p className="wr-title" title={item.title}>{item.title}</p>
                    {item.avatarName && <span className="wr-meta">数字人：{item.avatarName}</span>}
                    {item.voiceName  && <span className="wr-meta">声音：{item.voiceName}</span>}
                    {item.createdAt  && <span className="wr-date">{item.createdAt}</span>}
                  </div>

                  {/* Actions */}
                  <div className="wr-actions">
                    {isCompleted && item.videoUrl && (
                      <button className="wr-dl-btn" onClick={() => handleDownload(item)}>
                        下载
                      </button>
                    )}
                    <button className="wr-del-btn" onClick={handleDelete}>
                      删除
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="wr-pagination">
              <button
                className="wr-page-btn"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >←</button>

              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let n;
                if (totalPages <= 7)                   n = i + 1;
                else if (currentPage <= 4)              n = i + 1;
                else if (currentPage >= totalPages - 3) n = totalPages - 6 + i;
                else                                    n = currentPage - 3 + i;
                return (
                  <button
                    key={n}
                    className={`wr-page-num${currentPage === n ? ' active' : ''}`}
                    onClick={() => handlePageChange(n)}
                  >{n}</button>
                );
              })}

              <button
                className="wr-page-btn"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >→</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WorkflowResults;
